/* eslint-disable react-refresh/only-export-components */
/**
 * Direction Context
 * Manages RTL/LTR direction based on language
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDirection } from '../i18n';

import type { ReactNode } from 'react';

type Direction = 'ltr' | 'rtl';

interface DirectionContextType {
  direction: Direction;
  isRtl: boolean;
  setDirection: (dir: Direction) => void;
  toggleDirection: () => void;
}

const DirectionContext = createContext<DirectionContextType | undefined>(undefined);

export function DirectionProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [direction, setDirectionState] = useState<Direction>(() => getDirection(i18n.language));

  // Update direction when language changes
  useEffect(() => {
    const newDirection = getDirection(i18n.language);
    setDirectionState(newDirection);
  }, [i18n.language]);

  // Update document direction attribute
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;

    // Update body classes for RTL styling
    if (direction === 'rtl') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [direction, i18n.language]);

  const setDirection = (dir: Direction) => {
    setDirectionState(dir);
  };

  const toggleDirection = () => {
    setDirectionState((prev) => (prev === 'ltr' ? 'rtl' : 'ltr'));
  };

  const contextValue = useMemo(
    () => ({
      direction,
      isRtl: direction === 'rtl',
      setDirection,
      toggleDirection,
    }),
    [direction]
  );

  return (
    <DirectionContext.Provider value={contextValue}>
      <div dir={direction}>{children}</div>
    </DirectionContext.Provider>
  );
}

export function useDirection() {
  const context = useContext(DirectionContext);
  if (!context) {
    throw new Error('useDirection must be used within a DirectionProvider');
  }
  return context;
}

export { DirectionContext };
